import { DateWrapped, DateWrapper, DateWrapperProperty } from '../types/date-wrapper';

const DATE_WRAPPER_PROPERTY: DateWrapperProperty = 'dæt';

/**
 * 객체가 DateWrapper 타입인지 확인하는 타입 가드
 */
function isDateWrapper(obj: unknown): obj is DateWrapper {
  return (
    obj != null &&
    typeof obj === 'object' &&
    Object.prototype.hasOwnProperty.call(obj, DATE_WRAPPER_PROPERTY) &&
    typeof (obj as Record<string, unknown>)[DATE_WRAPPER_PROPERTY] === 'string' &&
    Object.keys(obj).length === 1
  );
}

/**
 * Date 객체를 DateWrapper로 재귀적으로 변환합니다.
 */
function wrapDateDeep<T>(target: T): DateWrapped<T> {
  if (target == null || typeof target !== 'object') {
    return target as DateWrapped<T>;
  }

  const mutableTarget = target as Record<string, unknown>;
  for (const key of Object.keys(target) as Array<keyof T>) {
    const val = target[key];
    if (typeof val === 'object' && val != null) {
      if (val instanceof Date) {
        mutableTarget[key as string] = { [DATE_WRAPPER_PROPERTY]: val.toISOString() };
      } else {
        mutableTarget[key as string] = wrapDateDeep(val as object);
      }
    }
  }
  return target as DateWrapped<T>;
}

/**
 * DateWrapper를 Date 객체로 재귀적으로 복원합니다.
 */
function unwrapDateDeep<T>(target: DateWrapped<T>): T {
  if (target == null || typeof target !== 'object') {
    return target as T;
  }

  const mutableTarget = target as Record<string, unknown>;
  for (const key of Object.keys(target) as Array<keyof DateWrapped<T>>) {
    const val = target[key];
    if (typeof val === 'object' && val != null) {
      if (isDateWrapper(val)) {
        mutableTarget[key as string] = new Date(val[DATE_WRAPPER_PROPERTY]);
      } else {
        mutableTarget[key as string] = unwrapDateDeep(val as object);
      }
    }
  }
  return target as T;
}

export function wrapDate<T>(target: T): DateWrapped<T> {
  if (target == null || typeof target !== 'object') {
    return target as DateWrapped<T>;
  }
  return wrapDateDeep(target);
}

export function unwrapDate<T>(target: DateWrapped<T>): T {
  if (target == null || typeof target !== 'object') {
    return target as T;
  }
  return unwrapDateDeep(target as DateWrapped<T>);
}
